import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Avatar,
  useTheme,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Support,
  Business,
  Feedback,
  TrackChanges,
  AttachFile,
  Close,
  ContactMail,
  SupportAgent,
  Search,
  List,
  Schedule,
  CheckCircle,
  Person,
  CalendarToday,
  PriorityHigh,
  Refresh
} from '@mui/icons-material';

const SupportCenter = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [publicTickets, setPublicTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  
  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Support Ticket State
  const [ticketForm, setTicketForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium'
  });
  
  // Track Ticket State
  const [trackForm, setTrackForm] = useState({
    ticketId: '',
    email: ''
  });
  
  const [attachment, setAttachment] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchPublicTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await fetch('https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/admin/support/tickets');
      if (response.ok) {
        const data = await response.json();
        setPublicTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  React.useEffect(() => {
    if (activeTab === 3) {
      fetchPublicTickets();
    }
  }, [activeTab]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        setSuccessMessage('Message sent successfully! We\'ll get back to you soon.');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('API failed');
      }
    } catch (error) {
      const mailtoLink = `mailto:contact.hypecircle@gmail.com?subject=${encodeURIComponent(contactForm.subject)}&body=${encodeURIComponent(
        `Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`
      )}`;
      window.open(mailtoLink);
      setSuccessMessage('Your email client will open with the message pre-filled.');
    }
    
    setShowSuccess(true);
    setIsSubmitting(false);
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/support/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketForm)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`Support ticket created! Ticket ID: ${result.ticketId}`);
        setTicketForm({ name: '', email: '', subject: '', message: '', priority: 'medium' });
        setAttachment(null);
      } else {
        throw new Error('API failed');
      }
    } catch (error) {
      const mailtoLink = `mailto:contact.hypecircle@gmail.com?subject=${encodeURIComponent(`[${ticketForm.priority.toUpperCase()}] ${ticketForm.subject}`)}&body=${encodeURIComponent(
        `Name: ${ticketForm.name}\nEmail: ${ticketForm.email}\nPriority: ${ticketForm.priority.toUpperCase()}\n\nMessage:\n${ticketForm.message}`
      )}`;
      window.open(mailtoLink);
      setSuccessMessage('Your email client will open with the support request pre-filled.');
    }
    
    setShowSuccess(true);
    setIsSubmitting(false);
  };

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`https://yiyuz8psi0.execute-api.ap-south-1.amazonaws.com/prod/api/support/track/${trackForm.ticketId}?email=${trackForm.email}`);
      
      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`Ticket Status: ${result.status} - ${result.message || 'No additional details'}`);
      } else {
        setSuccessMessage('Ticket not found. Please check your ticket ID and email.');
      }
    } catch (error) {
      setSuccessMessage('Unable to track ticket at the moment. Please contact support directly.');
    }
    
    setShowSuccess(true);
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: <Email />,
      title: "General Contact",
      info: "contact.hypecircle@gmail.com",
      description: "For general inquiries and support"
    },
    {
      icon: <Business />,
      title: "Business Inquiries",
      info: "hypecircle.team@gmail.com",
      description: "For partnerships and business matters"
    },
    {
      icon: <Support />,
      title: "24/7 Support",
      info: "Always Available",
      description: "We're here to help anytime"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          textAlign: 'center',
          mb: { xs: 3, sm: 6 },
          p: { xs: 2, sm: 4 },
          borderRadius: 3,
          background: isDark
            ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)'}`,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            background: isDark
              ? 'linear-gradient(45deg, #FFD700, #FFA500)'
              : 'linear-gradient(45deg, #3B82F6, #93C5FD)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Support Center
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
          Get help, submit tickets, and track your requests
        </Typography>
      </Box>

      {/* Contact Info Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 6 } }}>
        {contactInfo.map((contact, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              sx={{
                background: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-2px)' }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: isDark ? 'rgba(255, 215, 0, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                    color: isDark ? '#FFD700' : '#3B82F6',
                    mx: 'auto',
                    mb: 2,
                    width: 56,
                    height: 56
                  }}
                >
                  {contact.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {contact.title}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  {contact.info}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {contact.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Support Interface */}
      <Card
        sx={{
          background: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)} 
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<ContactMail />} label="Contact Us" />
            <Tab icon={<SupportAgent />} label="Support Ticket" />
            <Tab icon={<TrackChanges />} label="Track Ticket" />
            <Tab icon={<List />} label="Public Tickets" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Contact Us Tab */}
          {activeTab === 0 && (
            <Box component="form" onSubmit={handleContactSubmit}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Send us a Message
              </Typography>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<Send />}
                    disabled={isSubmitting}
                    sx={{
                      background: isDark
                        ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                        : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                      color: isDark ? '#000' : '#fff',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Support Ticket Tab */}
          {activeTab === 1 && (
            <Box component="form" onSubmit={handleTicketSubmit}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Create Support Ticket
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    value={ticketForm.name}
                    onChange={(e) => setTicketForm({...ticketForm, name: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={ticketForm.email}
                    onChange={(e) => setTicketForm({...ticketForm, email: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={ticketForm.priority}
                      onChange={(e) => setTicketForm({...ticketForm, priority: e.target.value})}
                      label="Priority"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Describe your issue"
                    multiline
                    rows={6}
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm({...ticketForm, message: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AttachFile />}
                    >
                      Attach Screenshot
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => setAttachment(e.target.files[0])}
                      />
                    </Button>
                    {attachment && (
                      <Chip
                        label={attachment.name}
                        onDelete={() => setAttachment(null)}
                        deleteIcon={<Close />}
                        color="primary"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<SupportAgent />}
                    disabled={isSubmitting}
                    sx={{
                      background: isDark
                        ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                        : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                      color: isDark ? '#000' : '#fff',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? 'Creating Ticket...' : 'Create Support Ticket'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Track Ticket Tab */}
          {activeTab === 2 && (
            <Box component="form" onSubmit={handleTrackSubmit}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Track Your Ticket
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Enter your ticket ID and email address to check the status of your support request.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ticket ID"
                    value={trackForm.ticketId}
                    onChange={(e) => setTrackForm({...trackForm, ticketId: e.target.value})}
                    placeholder="e.g., TKT-12345"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={trackForm.email}
                    onChange={(e) => setTrackForm({...trackForm, email: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<Search />}
                    disabled={isSubmitting}
                    sx={{
                      background: isDark
                        ? 'linear-gradient(45deg, #FFD700, #FFA500)'
                        : 'linear-gradient(45deg, #3B82F6, #1E40AF)',
                      color: isDark ? '#000' : '#fff',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? 'Tracking...' : 'Track Ticket'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Public Tickets Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                All Support Tickets
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                View all public support tickets and their current status.
              </Typography>
              
              {ticketsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  {publicTickets.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <List sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No tickets available
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {publicTickets.slice(0, 10).map((ticket) => (
                        <Grid item xs={12} key={ticket.id}>
                          <Card sx={{ 
                            background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                          }}>
                            <CardContent sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                  {ticket.subject}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Chip 
                                    label={ticket.priority?.toUpperCase() || 'MEDIUM'} 
                                    color={ticket.priority === 'urgent' ? 'error' : ticket.priority === 'high' ? 'warning' : 'info'}
                                    size="small"
                                  />
                                  <Chip 
                                    label={ticket.status?.replace('-', ' ').toUpperCase() || 'OPEN'}
                                    color={ticket.status === 'resolved' ? 'success' : ticket.status === 'in-progress' ? 'warning' : 'error'}
                                    size="small"
                                    icon={ticket.status === 'resolved' ? <CheckCircle /> : <Schedule />}
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, color: 'text.secondary' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Person fontSize="small" />
                                  <Typography variant="body2">{ticket.name}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {ticket.ticketId}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CalendarToday fontSize="small" />
                                  <Typography variant="body2">
                                    {new Date(ticket.createdAt).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={fetchPublicTickets}
                      disabled={ticketsLoading}
                      sx={{
                        borderColor: isDark ? 'rgba(255, 215, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)',
                        color: isDark ? '#FFD700' : '#3B82F6',
                      }}
                    >
                      Refresh Tickets
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SupportCenter;